import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAddCorrectiveAction,
  useCloseCorrectiveAction,
  useSafetyIncident,
} from "@/hooks/use-safety-incidents";
import { parseIncidentNarrative } from "@/lib/safety-incident-narrative";

const SEVERITY_LABELS: Record<string, string> = {
  none: "None",
  first_aid: "First aid",
  recordable: "Recordable",
  lost_time: "Lost time",
  fatal: "Fatal",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function SafetyIncidentDetailPage() {
  const rawParams = useParams({ strict: false });
  const id = typeof rawParams.id === "string" ? rawParams.id : "";
  const {
    data: incident,
    isLoading,
    isError,
    error,
    refetch,
  } = useSafetyIncident(id, id.length > 0);

  const addAction = useAddCorrectiveAction();
  const closeAction = useCloseCorrectiveAction();

  const [newActionDescription, setNewActionDescription] = useState("");
  const [newActionDueDate, setNewActionDueDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );

  const [closeDialog, setCloseDialog] = useState<{ actionId: string } | null>(null);
  const [closureNote, setClosureNote] = useState("");

  if (!id) {
    return (
      <div className="space-y-4">
        <Link
          to="/safety-incidents"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Safety Incidents
        </Link>
        <ErrorState message="Missing incident ID. Open this page from the safety incidents list." />
      </div>
    );
  }

  const narrative = incident ? parseIncidentNarrative(incident.description) : null;
  const canManageActions = incident && incident.status === "open" && !incident.isLocked;

  return (
    <div className="space-y-4 max-w-3xl">
      <Link
        to="/safety-incidents"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Safety Incidents
      </Link>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {!isLoading && isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load incident"}
          onRetry={() => void refetch()}
        />
      )}

      {!isLoading && !isError && !incident && (
        <ErrorState message="Incident not found." onRetry={() => void refetch()} />
      )}

      {!isLoading && !isError && incident && narrative && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Incident #{incident.incidentNumber}</h1>
              {incident.projectName ? (
                <p className="text-sm text-muted-foreground mt-1">{incident.projectName}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant={incident.status === "closed" ? "secondary" : "default"}>
                {incident.status === "closed" ? "Closed" : "Open"}
              </Badge>
              {incident.isLocked ? <Badge variant="outline">Locked</Badge> : null}
              <Badge variant="outline">
                {SEVERITY_LABELS[incident.injurySeverity] ?? incident.injurySeverity}
              </Badge>
            </div>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(incident.incidentDate)}</p>
                </div>
                {incident.incidentTime ? (
                  <div>
                    <p className="text-xs text-muted-foreground">Time</p>
                    <p className="font-medium">{incident.incidentTime}</p>
                  </div>
                ) : null}
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="font-medium">{incident.location}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Description</p>
                <p className="whitespace-pre-wrap">{narrative.main}</p>
              </div>
              {(narrative.personsInvolved ?? incident.personsInvolved) ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Persons involved</p>
                  <p className="whitespace-pre-wrap">
                    {narrative.personsInvolved ?? incident.personsInvolved}
                  </p>
                </div>
              ) : null}
              {(narrative.contributingFactors ?? incident.contributingFactors) ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Contributing factors</p>
                  <p className="whitespace-pre-wrap">
                    {narrative.contributingFactors ?? incident.contributingFactors}
                  </p>
                </div>
              ) : null}
              {(narrative.witnessStatements ?? incident.witnessStatements) ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Witness statements</p>
                  <p className="whitespace-pre-wrap">
                    {narrative.witnessStatements ?? incident.witnessStatements}
                  </p>
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground pt-2 border-t">
                Reported by {incident.createdByName ?? incident.createdByUserId}
                {" · "}
                {new Date(incident.createdAt).toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base">Corrective actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {incident.correctiveActions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No corrective actions recorded yet.
                </p>
              ) : (
                incident.correctiveActions.map((action) => (
                  <div key={action.id} className="border-b last:border-0 pb-4 last:pb-0">
                    <p className="text-sm font-medium">{action.description}</p>
                    <div className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                      {action.ownerName ? <span>Owner: {action.ownerName}</span> : null}
                      {action.dueDate ? <span>Due: {formatDate(action.dueDate)}</span> : null}
                      {action.completedAt ? (
                        <span className="sm:col-span-2 text-green-700 dark:text-green-400">
                          Completed {new Date(action.completedAt).toLocaleString()}
                          {action.completedByName ? ` · ${action.completedByName}` : ""}
                        </span>
                      ) : null}
                      {action.closureNote ? (
                        <span className="sm:col-span-2 text-sm text-foreground">
                          Closure: {action.closureNote}
                        </span>
                      ) : null}
                    </div>
                    {canManageActions && action.completedAt === null ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-3 min-h-[36px]"
                        onClick={() => {
                          setClosureNote("");
                          setCloseDialog({ actionId: action.id });
                        }}
                      >
                        Mark complete
                      </Button>
                    ) : null}
                  </div>
                ))
              )}

              {canManageActions ? (
                <form
                  className="space-y-3 rounded-lg border bg-muted/30 p-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const desc = newActionDescription.trim();
                    if (!desc || !id) return;
                    addAction.mutate(
                      {
                        incidentId: id,
                        description: desc,
                        dueDate: newActionDueDate || undefined,
                      },
                      {
                        onSuccess: () => {
                          setNewActionDescription("");
                          setNewActionDueDate(new Date().toISOString().slice(0, 10));
                        },
                      },
                    );
                  }}
                >
                  <p className="text-sm font-medium">Add corrective action</p>
                  <div className="space-y-2">
                    <Label htmlFor="ca-desc">Action *</Label>
                    <Textarea
                      id="ca-desc"
                      value={newActionDescription}
                      onChange={(e) => setNewActionDescription(e.target.value)}
                      placeholder="Describe the corrective action"
                      rows={2}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ca-due">Due date</Label>
                    <Input
                      id="ca-due"
                      type="date"
                      value={newActionDueDate}
                      onChange={(e) => setNewActionDueDate(e.target.value)}
                      className="min-h-[44px] max-w-[14rem]"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="sm"
                    className="min-h-[40px]"
                    disabled={addAction.isPending || !newActionDescription.trim()}
                  >
                    {addAction.isPending ? "Adding…" : "Add action"}
                  </Button>
                </form>
              ) : null}
            </CardContent>
          </Card>

          <Dialog
            open={closeDialog !== null}
            onOpenChange={(open) => {
              if (!open) setCloseDialog(null);
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Complete corrective action</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label htmlFor="closure-note">Closure note *</Label>
                <Textarea
                  id="closure-note"
                  value={closureNote}
                  onChange={(e) => setClosureNote(e.target.value)}
                  placeholder="Summarize verification or completion"
                  rows={3}
                  required
                />
              </div>
              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCloseDialog(null)}
                  disabled={closeAction.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={closeAction.isPending || !closureNote.trim() || !closeDialog}
                  onClick={() => {
                    if (!closeDialog || !id) return;
                    closeAction.mutate(
                      {
                        incidentId: id,
                        actionId: closeDialog.actionId,
                        closureNote: closureNote.trim(),
                      },
                      {
                        onSuccess: () => {
                          setCloseDialog(null);
                          setClosureNote("");
                        },
                      },
                    );
                  }}
                >
                  {closeAction.isPending ? "Saving…" : "Complete"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
